import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAppStore, type ViewType } from '../../store';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { Trophy, CalendarRange, LayoutGrid, BarChart3, Archive, CalendarDays, RefreshCw } from 'lucide-react';

const views: { key: ViewType; label: string; icon: typeof Trophy }[] = [
  { key: 'track', label: 'Track', icon: Trophy },
  { key: 'planner', label: 'Planner', icon: CalendarRange },
  { key: 'backlog', label: 'Backlog', icon: LayoutGrid },
  { key: 'summary', label: 'Summary', icon: BarChart3 },
  { key: 'archive', label: 'Archive', icon: Archive },
  { key: 'calendar', label: 'Calendar', icon: CalendarDays },
  { key: 'recurring', label: 'Recurring', icon: RefreshCw },
];

export function ViewSelector() {
  const { currentView, setView } = useAppStore();
  const isNarrow = useMediaQuery('(max-width: 1024px)');

  useEffect(() => {
    if (isNarrow && currentView === 'planner') {
      setView('track');
    }
  }, [isNarrow, currentView, setView]);

  const filteredViews = isNarrow ? views.filter(v => v.key !== 'planner') : views;

  return (
    <nav data-tutorial="view-selector" className="flex items-center gap-0.5">
      {filteredViews.map(({ key, label, icon: Icon }) => (
        <button
          key={key}
          onClick={() => setView(key)}
          aria-current={currentView === key ? 'page' : undefined}
          className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-caption font-semibold transition-all z-10 ${
            currentView === key
              ? 'text-text-primary'
              : 'text-text-muted hover:text-text-secondary hover:scale-105'
          }`}
        >
          {currentView === key && (
            <motion.div
              layoutId="viewIndicator"
              className="absolute inset-0 rounded-lg bg-accent/12 border border-accent/30 shadow-sm shadow-accent/10"
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            />
          )}
          <Icon size={14} className={`relative z-10 ${currentView === key ? 'text-accent' : ''}`} />
          <span className="relative z-10">{label}</span>
        </button>
      ))}
    </nav>
  );
}
